import { Button, Dialog, DialogActions, DialogContent, DialogTitle } from '@mui/material';
import TextField from '@mui/material/TextField';
import { QueryObserverResult, useMutation } from '@tanstack/react-query';
import React, { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { editEvacuation } from '../../api/mobileapi';
import { EditEvacuation, EvacuationData } from '../../api/types';
import { AdamsError, getErrorMessage } from '../../utils/errors';
import { useHomeData } from '../LoadHomeData/HomeDataProvider';
import { Severity, useToast } from '../ToastProvider/ToastProvider';
import { useTranslations } from '../Translations/TranslationsProvider';

interface EditEventDialogProps {
    event: EvacuationData;
    openEditDialog: boolean;
    handleCloseEditDialog: () => void;
    refetchEvacuation: () => Promise<QueryObserverResult<EvacuationData, AdamsError>>;
}

const MAX_CHARACTERS_TITLE = 24;
const MAX_CHARACTERS_DESCRIPTION = 80;

export const EditEvacuationDialog: React.FC<EditEventDialogProps> = ({
    event,
    openEditDialog,
    handleCloseEditDialog,
    refetchEvacuation,
}) => {
    const { translations } = useTranslations();
    const { currentLanguage } = useHomeData();
    const toast = useToast();

    const {
        register,
        reset,
        handleSubmit,
        formState: { errors },
    } = useForm<EditEvacuation>({
        defaultValues: {
            eventTitle: event.eventTitle,
            eventDescription: event.eventDescription,
        },
    });

    const { mutate: editEvent, isLoading: isEditing } = useMutation(editEvacuation, {
        onSuccess: async () => {
            await refetchEvacuation();
            toast(translations.EVACUATIONS_EDIT_MESSAGE, Severity.success);
            handleCloseEditDialog();
        },
        onError: (err: AdamsError) => {
            const message = getErrorMessage(err);
            if (message) toast(message, Severity.error);
        },
    });

    const handleEditEvent = (data: EditEvacuation) => {
        editEvent({ language: currentLanguage, eventId: event.eventID, data });
    };

    useEffect(() => {
        if (event) {
            reset({
                eventTitle: event.eventTitle,
                eventDescription: event.eventDescription,
            });
        }
    }, [event, reset, openEditDialog]);

    return (
        <Dialog
            data-testid="edit-dialog"
            open={openEditDialog}
            onClose={handleCloseEditDialog}
            closeAfterTransition={false}
            fullWidth
            maxWidth="sm"
        >
            <form onSubmit={handleSubmit(handleEditEvent)}>
                <DialogTitle>{translations.EVACUATIONS_EDIT}</DialogTitle>
                <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                    <TextField
                        data-testid="event-input-title"
                        fullWidth
                        label={translations.EVACUATIONS_EVENT_TITLE}
                        variant="standard"
                        required
                        {...register('eventTitle', {
                            maxLength: {
                                value: MAX_CHARACTERS_TITLE,
                                message: `${translations.MAX_CHARACTERS_VALIDATION_ERROR} ${MAX_CHARACTERS_TITLE}`,
                            },
                        })}
                        error={!!errors.eventTitle}
                        helperText={errors.eventTitle?.message}
                    />
                    <TextField
                        data-testid="event-input-description"
                        multiline
                        fullWidth
                        label={translations.EVACUATIONS_EVENT_DESCRIPTION}
                        variant="standard"
                        required
                        {...register('eventDescription', {
                            maxLength: {
                                value: MAX_CHARACTERS_DESCRIPTION,
                                message: `${translations.MAX_CHARACTERS_VALIDATION_ERROR} ${MAX_CHARACTERS_DESCRIPTION}`,
                            },
                        })}
                        error={!!errors.eventDescription}
                        helperText={errors.eventDescription?.message}
                    />
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleCloseEditDialog} color="secondary">
                        {translations.GLOBAL_CANCEL_BUTTON}
                    </Button>
                    <Button
                        data-testid="event-save-button"
                        disabled={isEditing}
                        color="primary"
                        variant="contained"
                        type="submit"
                    >
                        {translations.GLOBAL_SAVE_BUTTON}
                    </Button>
                </DialogActions>
            </form>
        </Dialog>
    );
};
